import React, { useState, useEffect } from 'react';
import { Upload, Trash2, Star, AlertCircle, Camera, Loader, CheckCircle, GripVertical, Info, Image, ChevronLeft, X } from 'lucide-react';
import { fetchApartmentImages, prepareImageUpload, uploadToR2, updateImage, deleteImage } from './api';
import { useAppTheme } from '../../contexts/ThemeContext';

const ApartmentPhotoManagement = ({ currentApartment, handleBackToApartments }) => {
  const { palette, Colors } = useAppTheme();
  const [images, setImages] = useState([]);
  const [loading, setLoading] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState(null);
  const [draggedIndex, setDraggedIndex] = useState(null);

  useEffect(() => { if (currentApartment) loadImages(); }, [currentApartment]);

  const loadImages = async () => {
    try { setLoading(true); const data = await fetchApartmentImages(currentApartment.id); setImages(data.sort((a, b) => a.order - b.order)); setError(null); }
    catch (err) { setError('Failed to load images: ' + err.message); }
    finally { setLoading(false); }
  };

  const handleFileSelect = async (e) => {
    const files = Array.from(e.target.files);
    if (!files.length) return;
    const validFiles = files.filter(f => {
      if (f.size > 5 * 1024 * 1024) { setError(`${f.name} is too large. Max 5MB.`); return false; }
      if (!f.type.startsWith('image/')) { setError(`${f.name} is not an image.`); return false; }
      return true;
    });
    if (!validFiles.length) return;
    setUploading(true); setError(null);
    for (const file of validFiles) {
      try {
        const { upload_url, public_url, image_id } = await prepareImageUpload(currentApartment.id, file.name, file.type);
        await uploadToR2(upload_url, file);
        const newImage = { id: image_id, url: public_url, order: images.length + 1, isCover: images.length === 0 };
        setImages(prev => [...prev, newImage]);
        if (images.length === 0) await updateImage(image_id, { is_cover: true });
      } catch (err) { setError(`Failed to upload ${file.name}: ${err.message}`); }
    }
    setUploading(false);
  };

  const handleSetCover = async (imageId) => {
    try {
      setLoading(true);
      setImages(images.map(img => ({ ...img, isCover: img.id === imageId })));
      await updateImage(imageId, { is_cover: true }); setError(null);
    } catch (err) { setError('Failed to set cover: ' + err.message); loadImages(); }
    finally { setLoading(false); }
  };

  const handleDelete = async (imageId) => {
    if (!window.confirm('Delete this image? This cannot be undone.')) return;
    try { setLoading(true); await deleteImage(imageId); setImages(prev => prev.filter(img => img.id !== imageId)); setError(null); }
    catch (err) { setError('Failed to delete: ' + err.message); }
    finally { setLoading(false); }
  };

  const handleDragStart = (i) => setDraggedIndex(i);
  const handleDragOver = (e, i) => {
    e.preventDefault(); if (draggedIndex === null || draggedIndex === i) return;
    const newImgs = [...images]; const dragged = newImgs[draggedIndex];
    newImgs.splice(draggedIndex, 1); newImgs.splice(i, 0, dragged);
    setImages(newImgs); setDraggedIndex(i);
  };
  const handleDragEnd = async () => {
    if (draggedIndex === null) return;
    try { for (const [idx, img] of images.entries()) await updateImage(img.id, { order: idx + 1 }); setError(null); }
    catch (err) { setError('Failed to update order: ' + err.message); loadImages(); }
    setDraggedIndex(null);
  };

  if (!currentApartment) return (
    <div className="min-h-screen flex items-center justify-center p-6" style={{ backgroundColor: palette.background.primary }}>
      <div className="rounded-xl p-8 shadow-sm text-center" style={{ backgroundColor: palette.background.secondary, border: `1px solid ${palette.border}` }}>
        <AlertCircle className="w-16 h-16 mx-auto mb-4" style={{ color: palette.text.muted }} />
        <h3 className="text-xl font-bold mb-2" style={{ color: palette.text.primary }}>No Property Selected</h3>
        <p style={{ color: palette.text.secondary }}>Please select a property to manage photos.</p>
      </div>
    </div>
  );

  const P   = Colors.accent.purple; // photo management uses purple as accent
  const bg  = palette.background;
  const txt = palette.text;
  const bdr = palette.border;

  return (
    <div className="min-h-screen" style={{ backgroundColor: bg.primary }}>
      {/* Header */}
      <div className="px-6 pb-4 -mt-6">
        <div className="rounded-xl shadow-sm p-6" style={{ backgroundColor: bg.secondary, border: `1px solid ${bdr}` }}>
          <button onClick={handleBackToApartments}
            className="flex items-center gap-2 mb-4 font-medium transition group px-3 py-1.5 rounded-md"
            style={{ color: txt.secondary, border: `1px solid ${bdr}` }}
            onMouseEnter={e => e.currentTarget.style.backgroundColor = bg.tertiary}
            onMouseLeave={e => e.currentTarget.style.backgroundColor = 'transparent'}>
            <ChevronLeft className="w-5 h-5 group-hover:-translate-x-1 transition-transform" />
            Back to All Properties
          </button>
          <div className="flex items-center gap-4">
            <div className="p-3 rounded-lg" style={{ backgroundColor: `${P}20` }}>
              <Camera className="w-8 h-8" style={{ color: P }} />
            </div>
            <div className="flex-1">
              <h1 className="text-3xl font-bold" style={{ color: P }}>Property Photos</h1>
              <p className="mt-1" style={{ color: txt.secondary }}>{currentApartment.name}</p>
            </div>
          </div>
        </div>
      </div>

      {/* Guidelines */}
      <div className="px-6 pb-4">
        <div className="rounded-xl p-5 shadow-sm" style={{ backgroundColor: `${Colors.primary.main}08`, border: `1px solid ${Colors.primary.main}25` }}>
          <div className="flex items-start gap-3">
            <div className="p-2 rounded-lg flex-shrink-0" style={{ backgroundColor: `${Colors.primary.main}15` }}>
              <Info className="w-5 h-5" style={{ color: Colors.primary.main }} />
            </div>
            <div className="flex-1">
              <h3 className="font-bold mb-2 text-base" style={{ color: txt.primary }}>Photo Guidelines for Best Results</h3>
              <ul className="text-sm space-y-1.5" style={{ color: txt.secondary }}>
                {['Upload high-quality, well-lit photos of the property exterior, common areas, and amenities',
                  'The cover image (marked with ⭐) appears in property listings and search results',
                  'Show the building exterior, parking, security features, gardens, and recreational facilities',
                  'Minimum 5 photos recommended • Maximum 5MB per image',
                  'Drag & drop images to reorder them — order matters for user experience!'].map((tip, i) => (
                  <li key={i} className="flex items-start gap-2">
                    <CheckCircle className="w-4 h-4 flex-shrink-0 mt-0.5" style={{ color: Colors.primary.main }} />
                    <span dangerouslySetInnerHTML={{ __html: tip.replace(/\*\*(.*?)\*\*/g, '<strong>$1</strong>') }} />
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </div>
      </div>

      {error && (
        <div className="px-6 pb-4">
          <div className="rounded-lg p-4 flex items-start gap-3 shadow-sm" style={{ backgroundColor: `${Colors.status.error}10`, border: `1px solid ${Colors.status.error}40` }}>
            <AlertCircle className="w-5 h-5 flex-shrink-0 mt-0.5" style={{ color: Colors.status.error }} />
            <p className="flex-1 font-medium" style={{ color: Colors.status.error }}>{error}</p>
            <button onClick={() => setError(null)} style={{ color: Colors.status.error }}><X className="w-5 h-5" /></button>
          </div>
        </div>
      )}

      {/* Main Content */}
      <div className="px-6 pb-6">
        <div className="rounded-xl shadow-sm overflow-hidden" style={{ backgroundColor: bg.secondary, border: `1px solid ${bdr}` }}>
          {/* Upload */}
          <div className="p-6" style={{ borderBottom: `1px solid ${bdr}` }}>
            <label className="flex flex-col items-center justify-center border-2 border-dashed rounded-xl p-8 cursor-pointer transition-all group"
              style={{ borderColor: bdr }}
              onMouseEnter={e => { e.currentTarget.style.borderColor = P; e.currentTarget.style.backgroundColor = `${P}08`; }}
              onMouseLeave={e => { e.currentTarget.style.borderColor = bdr; e.currentTarget.style.backgroundColor = 'transparent'; }}>
              <input type="file" multiple accept="image/*" onChange={handleFileSelect} className="hidden" disabled={uploading} />
              <div className="p-4 rounded-full mb-4 transition" style={{ backgroundColor: `${P}20` }}>
                {uploading ? <Loader className="w-8 h-8 animate-spin" style={{ color: P }} /> : <Upload className="w-8 h-8" style={{ color: P }} />}
              </div>
              <p className="text-lg font-semibold mb-1" style={{ color: txt.primary }}>{uploading ? 'Uploading...' : 'Click to upload property images'}</p>
              <p className="text-sm" style={{ color: txt.muted }}>or drag and drop • PNG, JPG up to 5MB each</p>
            </label>
          </div>

          {/* Grid */}
          <div className="p-6">
            {loading && images.length === 0 ? (
              <div className="text-center py-12">
                <Loader className="w-12 h-12 mx-auto mb-4 animate-spin" style={{ color: P }} />
                <p style={{ color: txt.secondary }}>Loading images...</p>
              </div>
            ) : images.length === 0 ? (
              <div className="text-center py-12">
                <div className="w-20 h-20 rounded-full flex items-center justify-center mx-auto mb-4" style={{ backgroundColor: bg.tertiary }}>
                  <Image className="w-10 h-10" style={{ color: txt.muted }} />
                </div>
                <h3 className="text-lg font-bold mb-2" style={{ color: txt.primary }}>No images yet</h3>
                <p style={{ color: txt.muted }}>Upload your first property image to get started</p>
              </div>
            ) : (
              <>
                <div className="flex items-center justify-between mb-4">
                  <p className="text-sm font-semibold" style={{ color: txt.secondary }}>{images.length} {images.length === 1 ? 'image' : 'images'} uploaded</p>
                  <p className="text-xs" style={{ color: txt.muted }}>💡 Drag images to reorder</p>
                </div>
                <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
                  {images.map((image, i) => (
                    <div key={image.id} draggable onDragStart={() => handleDragStart(i)} onDragOver={e => handleDragOver(e, i)} onDragEnd={handleDragEnd}
                      className={`relative group rounded-xl overflow-hidden aspect-square cursor-move transition-all hover:shadow-lg ${draggedIndex === i ? 'opacity-50 scale-95' : ''}`}
                      style={{ backgroundColor: bg.tertiary }}>
                      <img src={image.url} alt={`Property image ${i + 1}`} className="w-full h-full object-cover" />
                      <div className="absolute top-2 left-2 bg-black/50 backdrop-blur-sm p-1.5 rounded-lg opacity-0 group-hover:opacity-100 transition-opacity">
                        <GripVertical className="w-4 h-4 text-white" />
                      </div>
                      {image.isCover && (
                        <div className="absolute top-2 right-2 bg-yellow-500 text-white px-2.5 py-1 rounded-full text-xs font-bold flex items-center gap-1 shadow-lg">
                          <Star className="w-3 h-3 fill-white" /> Cover
                        </div>
                      )}
                      <div className="absolute bottom-2 left-2 bg-black/70 backdrop-blur-sm text-white px-2 py-1 rounded-full text-xs font-bold">#{i + 1}</div>
                      <div className="absolute inset-0 bg-black/60 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center gap-2">
                        {!image.isCover && (
                          <button onClick={() => handleSetCover(image.id)} disabled={loading}
                            className="bg-yellow-500 hover:bg-yellow-600 text-white p-2.5 rounded-lg transition disabled:opacity-50" title="Set as cover">
                            <Star className="w-5 h-5" />
                          </button>
                        )}
                        <button onClick={() => handleDelete(image.id)} disabled={loading}
                          className="bg-red-600 hover:bg-red-700 text-white p-2.5 rounded-lg transition disabled:opacity-50" title="Delete">
                          <Trash2 className="w-5 h-5" />
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
              </>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default ApartmentPhotoManagement;